import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useApolloClient } from '@apollo/client';
import { AuthContextType } from '../types';
import { getBasePath, getFullPath } from '../utils/pathUtils';

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const client = useApolloClient();

  // Check token on initial load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Simple validation - check if token exists
      // A more robust solution would decode and check expiration
      setIsAuthenticated(true);
    }
    setIsLoading(false); // Authentication check complete
  }, []);

  // Login function
  const login = (token: string) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
  };

  // Logout function with improved state management
  const logout = (message: string | null = null) => {
    // Remove token from storage
    localStorage.removeItem('token');

    // Stash the message BEFORE dropping the auth flag. Flipping the flag
    // re-renders PrivateRoute, which bounces to /login synchronously — so a
    // message written after the async clearStore() arrived too late, and the
    // login page showed the generic "please log in" notice instead of this
    // one (and left this one behind to surface on a later visit).
    if (message) {
      sessionStorage.setItem('authError', message);
    }

    // Update authentication state immediately
    setIsAuthenticated(false);

    // Clear Apollo cache and reset store
    client.clearStore().then(() => {
      // Only redirect if not already on login page
      const basePath = getBasePath();
      const loginPath = getFullPath('/login');
      if (!window.location.pathname.startsWith(`${basePath}/login`)) {
        window.location.href = loginPath;
      }
    });
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
